/**
 * @jsxRuntime classic
 * @jsx jsx
 * @jsxFrag jsx
 */

import { Suspense, useCallback, useEffect, useMemo, useRef, useState } from 'react';

import { ErrorBoundary } from 'react-error-boundary';
import { useIntl } from 'react-intl';
import { di } from 'react-magnetic-di';

import { type DocNode } from '@atlaskit/adf-schema';
import { IconButton } from '@atlaskit/button/new';
import { cssMap, cx, jsx } from '@atlaskit/css';
import { INPUT_METHOD } from '@atlaskit/editor-common/analytics';
// eslint-disable-next-line @atlaskit/editor/warn-no-restricted-imports
import { type EditorActions, WithEditorActions } from '@atlaskit/editor-core';
import { ComposableEditor, type EditorNextProps } from '@atlaskit/editor-core/composable-editor';
import { EditorContext } from '@atlaskit/editor-core/editor-context';
import type { JSONDocNode } from '@atlaskit/editor-json-transformer';
import { TextSelection } from '@atlaskit/editor-prosemirror/state';
import FeatureGates from '@atlaskit/feature-gate-js-client';
import ArrowUpIcon from '@atlaskit/icon/core/arrow-up';
import { fg } from '@atlaskit/platform-feature-flags';
import { Box, Inline, Stack } from '@atlaskit/primitives/compiled';
import UFOSegment from '@atlaskit/react-ufo/segment';
import UFOSuspense from '@atlaskit/react-ufo/suspense';
import { expValEquals } from '@atlaskit/tmp-editor-statsig/exp-val-equals';
import { token } from '@atlaskit/tokens';
import {
	useVoiceDictation,
	useVoiceDictationEditorHandler,
	VoiceDictationStopReason,
} from '@atlassian/ai-audio';
import {
	MAX_CHARACTERS_INPUT,
	useEditorPreset,
} from '@atlassian/conversation-assistant-content-renderer/controllers/editor';
import { useRovoMentionProvider } from '@atlassian/conversation-assistant-content-renderer/utils/mention-provider';
import { ExperienceName } from '@atlassian/conversation-assistant-instrumentation/constants/experience-tracker';
import { logException } from '@atlassian/conversation-assistant-instrumentation/services/sentry';
import {
	ExperienceFailure,
	ExperienceStart,
	ExperienceSuccess,
} from '@atlassian/conversation-assistant-instrumentation/ui/ExperienceTracker';
import {
	type Agent,
	type FileUploadResponseItem,
} from '@atlassian/conversation-assistant-service-api/types/assistance-service';
import { type AssistanceServiceInputParam } from '@atlassian/conversation-assistant-service/services/assistance-service/main';
import { useEditorPlaceholder } from '@atlassian/conversation-assistant-ui-components/controllers/useEditorPlaceholder';
import { useHighlightActionsEditorPlaceholder } from '@atlassian/conversation-assistant-ui-components/controllers/useHighlightActionsEditorPlaceholder';
import { AtlassianRelayEnvironmentProvider } from '@atlassian/relay-environment-provider';
import { SmartMentionResource } from '@atlassian/smart-mention-resource';

import { useConversationFiles } from '../../../../common/controllers/conversation-files';
import { ChatInputActionButtons } from '../../../../common/ui/chat-input-action-buttons';
import { ComposableEditorWithSkills } from '../../../../common/ui/composable-editor';
import { InputContextListDisplay } from '../../../../common/ui/input-context-list-display';
import { VoiceActionsPanel } from '../../../../common/ui/voice-control-panels/voice-actions-panel';
import { VoiceRecordingPanel } from '../../../../common/ui/voice-control-panels/voice-recording-panel';
import { getActualTextContent } from '../../../../common/utils/node';
import { type RovoApp, useChatModeStoreActions } from '../../../../controllers/chat-mode-store';
import type { PromptContentChangePayload } from '../../prompt-content-change-payload';

import { LabsLozenge, type LabsLozengeVariant } from './labs-lozenge';
import messages from './messages';
import { hasRovoSkillInDoc } from './utils';

export type ArrowKeyPluginConfigMessage = {
	id: string;
	content: string | DocNode;
};

type PromptViewInternalProps = {
	productKey: string;
	siteId: string;
	orgId?: string;
	source: string;
	onSubmit: (params: {
		prompt: DocNode;
		deepResearchEnabled: boolean;
		thinkDeeperEnabled: boolean;
		fastModeEnabled: boolean;
		webSearchEnabled: boolean;
		appFilters?: RovoApp[];
		files?: FileUploadResponseItem[];
		addCurrentPageAsContextUserPreference?: boolean;
	}) => void;
	actions: EditorActions;
	onAgentSelect: (agent: Agent) => void;
	isBrowseAgentsModalOpen: boolean;
	setIsBrowseAgentsModalOpen: (isBrowseAgentsModalOpen: boolean) => void;
	enableAgents?: boolean;
	isVoiceDictationEnabled?: boolean;
	showMauiMediaPlaceholder?: boolean;
	/**
	 * Temporary prop for labs tag use with editor only
	 * @deprecated
	 */
	showLabsLozenge?: boolean;
	labsLozengeVariant?: LabsLozengeVariant;
	agent?: Agent | null;
	popupsMountPoint?: HTMLElement | null;
	useShortMessages?: boolean;
	compact?: boolean;
	onPromptContentChange?: (payload: PromptContentChangePayload) => void;
	defaultValue?: JSONDocNode;
};

export type ChatPromptInputProps = Omit<PromptViewInternalProps, 'actions'>;
const STORE_KEY = 'inline_prompt_view';
const styles = cssMap({
	stack: {
		width: '100%',
		marginBottom: token('space.negative.150'),
	},

	stackWithoutContexts: {
		display: 'contents',
	},

	editorWrapper: {
		maxWidth: '100%',
		display: 'flex',
		alignItems: 'center',
		// Vertically center the editor when on a single line
		paddingTop: token('space.050'),
		marginBottom: token('space.negative.050'),
		// Prevent the actions from growing when inline with the editor
		flexGrow: 99999,
	},

	// Remove padding from the editor content container in compact mode
	editorWrapperCompact: {
		// @ts-expect-error - nested .ak-editor-content-area selector not supported in cssMap strict types; required by ProseMirror editor styling
		// eslint-disable-next-line @atlaskit/ui-styling-standard/no-nested-selectors
		'.ak-editor-content-area': {
			paddingTop: token('space.0'),
			paddingRight: token('space.0'),
			paddingBottom: token('space.0'),
			paddingLeft: token('space.0'),
		},
	},

	actions: {
		justifyContent: 'end',
		gap: token('space.100'),
		// Expand to full width when not on the same row as the editor
		flexGrow: 1,
	},

	labsLozenge: {
		alignSelf: 'center',
	},
});

const ACTIVE_AND_NON_BOT_USER_SEARCH_FILTER =
	'account_status:"active" AND (NOT email_domain:"connect.atlassian.com") AND (NOT account_type:app)';

const PromptViewInternal = ({
	productKey,
	siteId,
	orgId,
	source,
	onSubmit,
	actions,
	onAgentSelect,
	isBrowseAgentsModalOpen,
	setIsBrowseAgentsModalOpen,
	enableAgents = false,
	showLabsLozenge = false,
	labsLozengeVariant = 'default',
	showMauiMediaPlaceholder = false,
	agent,
	popupsMountPoint,
	useShortMessages = false,
	compact = false,
	isVoiceDictationEnabled = false,
	onPromptContentChange,
	defaultValue,
}: PromptViewInternalProps) => {
	di(
		ComposableEditor,
		SmartMentionResource,
		useEditorPlaceholder,
		useEditorPreset,
		useChatModeStoreActions,
		AtlassianRelayEnvironmentProvider,
	);

	const saveRef = useRef<() => void>();
	const [inputValue, setInputValue] = useState('');
	const [hasMentionOrInlineCard, setHasMentionOrInlineCard] = useState(false);

	const defaultHasSkill = useMemo(() => hasRovoSkillInDoc(defaultValue), [defaultValue]);
	const [hasSkill, setHasSkill] = useState(defaultHasSkill);
	useEffect(() => {
		if (defaultHasSkill) {
			setHasSkill(true);
		}
	}, [defaultHasSkill]);
	const submitButtonRef = useRef<HTMLButtonElement>(null);
	const { value: mentionResourceOverride } = useRovoMentionProvider();
	const { placeholder, clearPlaceholder } = useEditorPlaceholder();
	const { highlightActionsEditorPlaceholder, clearHighlightActionsEditorPlaceholder } =
		useHighlightActionsEditorPlaceholder();
	const { formatMessage } = useIntl();
	const placeholderText = showMauiMediaPlaceholder
		? formatMessage(messages.chatInputAssistiveLabel)
		: undefined;

	const assistanceServiceParams: AssistanceServiceInputParam['assistanceServiceParams'] = {
		siteId,
		experienceId: 'ai-mate',
		product: productKey,
		touchpointSource: source,
	};

	const { files, clearAttachments, anyFilesUploading } = useConversationFiles({
		assistanceServiceParams,
		key: STORE_KEY,
	});

	const { getChatMode, resetReasoningMode, setWebSearchEnabled } = useChatModeStoreActions();

	const mentionResource = useMemo(
		() =>
			Promise.resolve(
				mentionResourceOverride ||
					new SmartMentionResource({
						productKey,
						siteId,
						includeTeams: true,
						includeUsers: true,
						orgId,
						searchQueryFilter: ACTIVE_AND_NON_BOT_USER_SEARCH_FILTER,
						// TODO: searchQueryFilter: fg('rovo_chat_filter_bot_users_mention_resource')
						// 	? ACTIVE_AND_NON_BOT_USER_SEARCH_FILTER
						// 	: undefined,
					}),
			),
		[productKey, siteId, orgId, mentionResourceOverride],
	);

	const inputExceedsMaxLimit = inputValue.length > MAX_CHARACTERS_INPUT;
	const isEmpty = inputValue.trim().length === 0;

	const isSendDisabled =
		inputExceedsMaxLimit || anyFilesUploading || (isEmpty && !hasMentionOrInlineCard && !hasSkill);

	const handleSubmit = useCallback(async () => {
		const value: DocNode = await actions.getValue();

		if (isSendDisabled) {
			return;
		}

		const {
			deepResearchEnabled,
			thinkDeeperEnabled,
			fastModeEnabled,
			webSearchEnabled,
			appFilters,
		} = getChatMode(STORE_KEY);
		const validFiles = files.filter((file) => !file.error);

		onSubmit({
			prompt: value,
			deepResearchEnabled,
			thinkDeeperEnabled,
			fastModeEnabled,
			webSearchEnabled,
			appFilters: appFilters,
			files: validFiles,
			addCurrentPageAsContextUserPreference: true,
		});

		// Clean up
		actions.clear();
		clearAttachments(STORE_KEY);
		resetReasoningMode(STORE_KEY);
		setWebSearchEnabled(STORE_KEY, false);
	}, [
		actions,
		clearAttachments,
		files,
		getChatMode,
		isSendDisabled,
		onSubmit,
		resetReasoningMode,
		setWebSearchEnabled,
	]);

	useEffect(() => {
		return () => {
			// Reset attachments on unmount
			clearAttachments(STORE_KEY);
		};
	}, [clearAttachments]);

	saveRef.current = handleSubmit;

	const { editorApi, preset } = useEditorPreset({
		onSave: () => saveRef.current?.(),
		arrowKeyPluginConfig: {
			userMessages: [],
		},
		productKey,
		siteId,
		useShortMessages,
		placeholderText,
	});

	const {
		onVoiceDictationUpdate,
		rejectTranscriptedText,
		disposeTranscriptedText,
	} = useVoiceDictationEditorHandler(editorApi);

	const [voiceDictationResult, { startRecording, stopRecording }] = useVoiceDictation(
		onVoiceDictationUpdate,
		siteId,
	);

	const onStartRecording = useCallback(() => {
		// TODO: Set recording placeholder
		startRecording();
	}, [startRecording]);

	const onAcceptRecording = useCallback(() => {
		disposeTranscriptedText();
		stopRecording(VoiceDictationStopReason.StoppedAccepted);
	}, [stopRecording, disposeTranscriptedText]);

	const onRejectRecording = useCallback(() => {
		stopRecording(VoiceDictationStopReason.StoppedRejected);
		editorApi?.core.actions.execute(({ tr }) => rejectTranscriptedText(tr));
	}, [stopRecording, editorApi?.core.actions, rejectTranscriptedText]);

	const closeTypeAhead = useCallback(() => {
		editorApi?.typeAhead?.actions.close({ insertCurrentQueryAsRawText: false });
	}, [editorApi?.typeAhead?.actions]);

	const openMentionTypeAhead = useCallback(() => {
		closeTypeAhead();
		// Small delay to ensure the editor regains focus after closing the dropdown menu
		// This prevents the typeahead from immediately closing in Safari
		// Original root cause research: https://product-fabric.atlassian.net/browse/AFC-550
		requestAnimationFrame(() => {
			editorApi?.core.actions.focus();
			editorApi?.mention?.actions.openTypeAhead(INPUT_METHOD.QUICK_INSERT);
		});
	}, [closeTypeAhead, editorApi?.core.actions, editorApi?.mention?.actions]);

	const openLinkTypeAhead = useCallback(() => {
		closeTypeAhead();
		const command = editorApi?.hyperlink?.commands.showLinkToolbar(INPUT_METHOD.QUICK_INSERT);
		editorApi?.core.actions.execute(command);
	}, [closeTypeAhead, editorApi?.core.actions, editorApi?.hyperlink?.commands]);

	const openAddTypeAhead = useCallback(() => {
		closeTypeAhead();
		// Small delay to ensure the editor regains focus after closing the dropdown menu
		// This prevents the typeahead from immediately closing in Safari
		// Original root cause research: https://product-fabric.atlassian.net/browse/AFC-550
		requestAnimationFrame(() => {
			editorApi?.core.actions.focus();
			editorApi?.quickInsert?.actions.openTypeAhead(INPUT_METHOD.QUICK_INSERT);
		});
	}, [closeTypeAhead, editorApi?.core.actions, editorApi?.quickInsert?.actions]);

	useEffect(() => {
		if (!!placeholder && !!editorApi) {
			actions.clear();
			actions.focus();
			closeTypeAhead();
			actions.appendText(placeholder.prompt);

			editorApi?.core.actions.execute(({ tr }) => {
				const maxPos = tr.doc.content.size;
				const startPos = Math.min(placeholder.selectionStartIndex, maxPos);
				const endPos = Math.min(placeholder.selectionEndIndex, maxPos);

				const transaction = tr.setSelection(
					TextSelection.between(tr.doc.resolve(startPos), tr.doc.resolve(endPos)),
				);

				return transaction;
			});

			switch (placeholder.type) {
				case 'person':
					openMentionTypeAhead();
					break;
				case 'link':
					openLinkTypeAhead();
					break;
			}

			clearPlaceholder();
		}
	}, [
		editorApi,
		actions,
		clearPlaceholder,
		closeTypeAhead,
		editorApi?.core.actions,
		openLinkTypeAhead,
		openMentionTypeAhead,
		placeholder,
	]);

	useEffect(() => {
		if (!!highlightActionsEditorPlaceholder && !!editorApi) {
			actions.clear();
			actions.focus();
			closeTypeAhead();
			editorApi.core.actions.replaceDocument(highlightActionsEditorPlaceholder);
			editorApi?.core.actions.execute(({ tr }) => {
				const lastNode = tr.doc.lastChild;
				if (lastNode) {
					const lastNodePos = tr.doc.resolve(tr.doc.content.size - lastNode.nodeSize);
					const endPos = tr.doc.resolve(tr.doc.content.size);
					const transaction = tr.setSelection(TextSelection.between(lastNodePos, endPos));

					return transaction;
				}

				return null;
			});
			// This is a workaround to avoid the focus being lost in Jira
			if (productKey === 'jira') {
				requestAnimationFrame(() => {
					setTimeout(() => {
						actions.focus();
					}, 1500);
				});
			}
			clearHighlightActionsEditorPlaceholder();
		}
	}, [
		editorApi,
		actions,
		clearHighlightActionsEditorPlaceholder,
		closeTypeAhead,
		highlightActionsEditorPlaceholder,
		productKey,
	]);

	const isConversationAgentic = enableAgents && !!agent;

	const editorProps: EditorNextProps = useMemo(() => {
		return {
			assistiveLabel: formatMessage(
				useShortMessages ? messages.chatInputAssistiveLabelShort : messages.chatInputAssistiveLabel,
			),
			minHeight: 20,
			shouldFocus: true,
			preset: preset,
			mentionProvider: mentionResource,
			maxHeight: 200,
			appearance: 'chromeless',
			onSave: () => saveRef.current?.(),
			popupsMountPoint: popupsMountPoint ?? undefined,
			defaultValue,
			onChange: (ev, meta) => {
				let hasMentionOrCard = false;
				ev.state.doc.descendants((node) => {
					if (
						node.type.name === 'mention' ||
						node.type.name === 'inlineCard' ||
						node.type.name === 'inlineExtension'
					) {
						hasMentionOrCard = true;
						return false;
					}
					return true;
				});

				if (!meta.isDirtyChange) {
					setHasMentionOrInlineCard(hasMentionOrCard);
				}
				setInputValue(
					fg('rovo_chat_inline_editor_count_skill_as_text')
						? getActualTextContent(ev.state.doc)
						: ev.state.doc.textContent,
				);

				onPromptContentChange?.({
					textLength: ev.state.doc.textContent.length,
					isEmpty: ev.state.doc.textContent.trim().length === 0,
					hasMentionOrInlineCard: hasMentionOrCard,
				});
			},
		};
	}, [
		formatMessage,
		useShortMessages,
		mentionResource,
		onPromptContentChange,
		preset,
		popupsMountPoint,
		setHasMentionOrInlineCard,
		setInputValue,
		defaultValue,
	]);

	return (
		<>
			<Stack xcss={cx(styles.stack, files.length === 0 && styles.stackWithoutContexts)}>
				<Box
					xcss={cx(
						styles.editorWrapper,
						compact &&
							expValEquals(
								'platform_editor_comment_rovoinlinechat_improvement',
								'isEnabled',
								true,
							) &&
							styles.editorWrapperCompact,
					)}
				>
					<ErrorBoundary
						fallbackRender={({ error }) => (
							<>
								<ExperienceFailure error={error} name={ExperienceName.LOAD_INLINE_EDITOR} />
								{fg('rovo_chat_enable_skills_ui_m1') && (
									<ExperienceFailure
										error={error}
										name={ExperienceName.LOAD_INLINE_CHAT_PROMPT_INPUT_SKILLS}
									/>
								)}
								{/* fallback to editor without skills */}
								<ComposableEditor {...editorProps} />
							</>
						)}
						onError={(error) => {
							logException({
								exception: error,
								name: ExperienceName.LOAD_INLINE_EDITOR,
								tags: {
									siteId: siteId,
								},
							});
						}}
					>
						<ExperienceStart name={ExperienceName.LOAD_INLINE_EDITOR} />
						{fg('rovo_chat_enable_skills_ui_m1') ? (
							<>
								<ExperienceStart name={ExperienceName.LOAD_INLINE_CHAT_PROMPT_INPUT_SKILLS} />
								<AtlassianRelayEnvironmentProvider>
									{/*
									 * Whether to strip the UFO Suspense hold for the skills query is
									 * gated by the `rovo_chat_strip_skills_ufo_suspense` experiment
									 * so we can measure the Rovo Chat TTAI win on real traffic.
									 *
									 * - Treatment (`isEnabled = true`): React's plain Suspense + a
									 *   name-only UFOSegment. The skills query no longer contributes to
									 *   Rovo Chat TTAI; the segment is still visible in the UFO panel
									 *   for debugging.
									 * - Control (`isEnabled = false`): original UFO Suspense with an
									 *   `interactionName`, which adds a TTAI hold (the pre-experiment
									 *   behavior).
									 */}
									{(() => {
										// Shared between both arms of the experiment so prop changes during
										// the experiment only need to be made in one place.
										const renderSkillsEditor = () => (
											<>
												<ComposableEditorWithSkills
													editorApi={editorApi}
													isConversationAgentic={isConversationAgentic}
													promptHasSkill={hasSkill}
													enableSkills
													siteId={siteId}
													{...editorProps}
												/>
												<ExperienceSuccess
													name={ExperienceName.LOAD_INLINE_CHAT_PROMPT_INPUT_SKILLS}
												/>
											</>
										);
										return FeatureGates.getExperimentValue(
											'rovo_chat_strip_skills_ufo_suspense',
											'isEnabled',
											false,
										) ? (
											<UFOSegment name="inline-chat-prompt-input-skills">
												<Suspense fallback={<ComposableEditor {...editorProps} />}>
													{renderSkillsEditor()}
												</Suspense>
											</UFOSegment>
										) : (
											<UFOSuspense
												interactionName="inline-chat-prompt-input-skills-load"
												fallback={<ComposableEditor {...editorProps} />}
											>
												{renderSkillsEditor()}
											</UFOSuspense>
										);
									})()}
								</AtlassianRelayEnvironmentProvider>
							</>
						) : (
							<ComposableEditor {...editorProps} />
						)}
						<ExperienceSuccess name={ExperienceName.LOAD_INLINE_EDITOR} />
					</ErrorBoundary>
				</Box>
				<InputContextListDisplay attachtmentsStoreKey={STORE_KEY} />
			</Stack>
			<Inline xcss={styles.actions}>
				{showLabsLozenge && (
					<Inline xcss={styles.labsLozenge}>
						<LabsLozenge variant={labsLozengeVariant} />
					</Inline>
				)}
				<Inline alignBlock="center" space="space.050">
					<VoiceRecordingPanel state={voiceDictationResult.state}>
						<ChatInputActionButtons
							siteId={siteId}
							attachmentsAndCurrentPageAsContextStoreKey={STORE_KEY}
							isDisabled={false}
							useCurrentPageContext={false}
							onLinkTypeAheadClick={openLinkTypeAhead}
							onMentionTypeAheadClick={openMentionTypeAhead}
							onAddTypeAheadClick={openAddTypeAhead}
							assistanceServiceParams={assistanceServiceParams}
							placement="bottom-end"
							onMenuOpen={closeTypeAhead}
							onAgentSelect={enableAgents ? onAgentSelect : undefined}
							isBrowseAgentsModalOpen={isBrowseAgentsModalOpen}
							setIsBrowseAgentsModalOpen={setIsBrowseAgentsModalOpen}
						/>
					</VoiceRecordingPanel>
				</Inline>

				<VoiceActionsPanel
					state={voiceDictationResult.state}
					onStartRecording={onStartRecording}
					onRejectRecording={onRejectRecording}
					onAcceptRecording={onAcceptRecording}
					isVoiceDictationEnabled={isVoiceDictationEnabled}
				>
					<IconButton
						isTooltipDisabled={false}
						appearance="primary"
						shape="circle"
						ref={submitButtonRef}
						onClick={() => saveRef.current?.()}
						type="submit"
						isDisabled={isSendDisabled}
						interactionName="ai-mate.send-message-button"
						icon={(iconProps) => <ArrowUpIcon {...iconProps} label={''} />}
						label={formatMessage(messages.sendButtonLabel)}
					/>
				</VoiceActionsPanel>
			</Inline>
		</>
	);
};

export const PromptView = (props: ChatPromptInputProps): JSX.Element => {
	return (
		<EditorContext>
			<WithEditorActions
				render={(actions) => <PromptViewInternal {...props} actions={actions} />}
			/>
		</EditorContext>
	);
};
